/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import { Checkbox } from "../../../components/ui/checkbox";
import { Label } from "../../../components/ui/label";
import { Button } from "../../../components/ui/button";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { useNgX2ConnectionsData } from "./hooks";
import { prepareNgX2connectionsDataGrid, tableContainerSxProps } from "../helpers";
import CustomDataGrid from "../../../components/Shared/Table/CustomDataGrid";
import { useSelector } from "react-redux";
import { useStoreDispatch } from "../../../store/hooks";
import { DIALOGOPENFOR } from "../../../globals/globals";
import { constStr } from "../../../globals/constants";
import ConfirmDialog from "../../../components/Shared/Dialogs/ConfirmDialog";
import { setOpenCustomDailog, setConfirmDialog } from "../../../store/helpers/helperReducers";
import { customDialogSelector, confirmDialogSelector } from "../../../store/helpers/helperSelectors";
import { setIsAddNewNgX2Connection, setSelectedNgX2Connection } from "../../../store/api/deviceConfig/reducers";
import AddEditNgX2Connection from "../../../components/Configuration/X2Connection/NgX2Connection/AddEditNgX2Connection";
import { IFullDeviceDetails, IngpeerInterfaces } from "../../../store/api/deviceConfig/types";
import { Pencil, X, Check } from "lucide-react";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { InputField } from "../../../components/Shared/Radix/Inputs/InputField";
import { Icon } from "../../../components/Shared/Icon";

interface INgConnection {
  portVal?: number;
  isEnable?: boolean;
}

const NgX2Connections: React.FC = () => {
  const dispatch = useStoreDispatch();
  const { data, isLoading, permissions, ngX2connectionsColumns, selectedNgX2Connection, deleteNgX2ConnectionHandler, handleUpdate } =
    useNgX2ConnectionsData();

  const getDataFromApi = data?.data[0]?.ngpeerInterfaces || [];
  const isEnableX2Connections = (data?.data[0]?.enable?.toLowerCase() === "false" ? false : true) || false;
  const localPortX2Connections = data?.data[0]?.localPort || "";
  const rowsData = prepareNgX2connectionsDataGrid(getDataFromApi);
  const noOfRows = data?.totalItems || 0;
  const [NoOfSelectedRow, setNoOfSelectedRow] = React.useState(0);
  const [selectionModel, setSelectionModel] = React.useState<GridRowSelectionModel>([]);
  const [modelOpenMode, setModelOpenMode] = React.useState("Add");
  const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const isOfflineDevice = selectedDevice?.configurationStatus?.toLowerCase() === "offline";
  const [initialConfig, setInitialConfig] = React.useState<INgConnection>({ portVal: 0, isEnable: false });

  const openCustomDialog = useSelector(customDialogSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);

  const handleOnAddNgX2Connection = (event: any) => {
    setModelOpenMode("Add");
    dispatch(setIsAddNewNgX2Connection(true));
    dispatch(setOpenCustomDailog(true));
  };

  const handleOnEdit = (event: any) => {
    setModelOpenMode("Edit");
    dispatch(setIsAddNewNgX2Connection(false));
    dispatch(setOpenCustomDailog(true));
    setSelectionModel([]);
    setNoOfSelectedRow(0)
  };

  const handleOnRowSelected = (rowIds: GridRowSelectionModel) => {
    const rowIdLength = rowIds?.length;
    setNoOfSelectedRow(rowIdLength);
    setSelectionModel(rowIds)
    if (rowIdLength === 1) {
      const selectedData = rowsData.filter((item) => item.index === rowIds[0])[0];
      //const selectedRowData = {...initialState.selectedNgX2Connection, ...selectedData};
      dispatch(setSelectedNgX2Connection(selectedData));
    }
  };

  const handleOnDelete = () => {
    dispatch(setConfirmDialog(DIALOGOPENFOR.NGX2CONNECTION));
  };

  const [isDisable, setIsDisable] = React.useState<any>(true);
  const [ngxEditVal, setNgxEditVal] = React.useState<INgConnection>({
    portVal: 0,
    isEnable: false,
  });

  const handlePortChange = (name: string, value: string) => {
    setNgxEditVal((prev) => ({
      ...prev,
      portVal: Number(value),
    }));
  };

  const handleUpdateConfig = () => {
    const updateObj: any = {};
    if (initialConfig.isEnable !== ngxEditVal.isEnable) {
      updateObj["Enable"] = ngxEditVal.isEnable;
    }
    if (initialConfig.portVal !== ngxEditVal.portVal) {
      updateObj["LocalPort"] = ngxEditVal.portVal;
    }
    if (initialConfig.isEnable && ngxEditVal.isEnable === false) {
      updateObj["Enable"] = false;
    }
    handleUpdate(updateObj, initialConfig.isEnable, initialConfig.portVal);
    setIsDisable(true);
    setInitialConfig(ngxEditVal);
  };

  React.useEffect(() => {
    if (!isLoading && data && Array.isArray(data.data) && data.data.length > 0) {
      const enableVal = data.data[0]?.enable?.toLowerCase() !== "false";
      const portVal = Number(data.data[0]?.localPort);

      const fetchedVal = { portVal, isEnable: enableVal };

      setNgxEditVal(fetchedVal);
      setInitialConfig(fetchedVal);
    }
  }, [data, isLoading]);

  return (
    <>
      {openCustomDialog && <AddEditNgX2Connection modelOpenMode={modelOpenMode} selectedNgX2Connection={selectedNgX2Connection} />}
      {openConfirmDialog === DIALOGOPENFOR.NGX2CONNECTION && (
        <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={deleteNgX2ConnectionHandler}>
          {`Are you sure you want to delete NG X2 Connection Peer ${selectedNgX2Connection.index}?`}
        </ConfirmDialog>
      )}
      <div>
        <div
          style={{
            minHeight: "300px",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius)",
            padding: "18px",
            boxShadow: "var(--shadow-card)",
            backgroundColor: "var(--card)",
          }}
        >
          <div className="flex flex-row">
            <div className="w-1/2">
              <div className="flex items-center pb-2 h-10">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="isEnable"
                    disabled={isDisable}
                    checked={ngxEditVal?.isEnable}
                    onCheckedChange={(checked: boolean) =>
                      setNgxEditVal((prev) => ({ ...prev, isEnable: !!checked }))
                    }
                    className="m-0"
                  />
                  <Label htmlFor="isEnable" className="cursor-pointer text-foreground">
                    Enable X2 connections
                  </Label>
                </div>
                <Label htmlFor="portVal" className="cursor-pointer text-foreground ml-6">
                  Local port:
                </Label>

                {isDisable ? (
                  <Label htmlFor="portVals" className="cursor-pointer text-foreground ml-6">
                    {ngxEditVal?.portVal}
                  </Label>
                ) : (
                  <div className="w-40" >
                    <InputField
                      label=""
                      name="portVal"
                      type="number"
                      value={ngxEditVal?.portVal ?? 0}
                      onChange={handlePortChange}
                    />
                  </div>
                )}

                {isDisable && permissions?.hasOpCodeGroup("manage_topology") && !isOfflineDevice && (
                  <Pencil
                    onClick={() => {
                      setIsDisable(!isDisable);
                    }}
                    className="h-4 w-4 ml-10 cursor-pointer text-primary"
                  />
                )}
                {!isDisable && (
                  <div className="ml-5 flex items-center">
                    <X onClick={() => setIsDisable(true)} className="h-4 w-4 ml-1 cursor-pointer text-destructive" />
                    <Check onClick={() => handleUpdateConfig()} className="h-4 w-4 ml-1 cursor-pointer text-green-500" />
                  </div>
                )}

                {permissions?.hasOpCodeGroup("manage_topology") && NoOfSelectedRow === 1 && (
                  <div className="flex gap-2 items-center ml-6">
                    <Button variant="secondary" size="sm" onClick={handleOnEdit}>
                      Edit
                    </Button>
                    <Button variant="secondary" size="sm" onClick={handleOnDelete}>
                      <Icon name="delete" size={16} />
                      Delete
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div className="w-1/2">
              <div className="p-2 w-full flex items-center justify-end">
                {permissions?.hasOpCodeGroup("manage_topology") && !isOfflineDevice && (
                  <Button
                    className="h-10 px-4 text-sm font-medium border border-input bg-background shadow-sm flex items-center gap-2 hover:bg-muted hover:text-accent-foreground transition-colors"
                    variant="outline"
                    onClick={handleOnAddNgX2Connection}
                  >
                    <Icon name="add" size={18} />
                    Add NG X2 Connection Peer
                  </Button>
                )}
              </div>
            </div>
          </div>
          <hr className="border-t border-border" />
          <div className="mt-3" style={tableContainerSxProps as React.CSSProperties}>
            <>
              {NoOfSelectedRow > 0 && (
                <strong className="pl-2">
                  {NoOfSelectedRow} row selected
                </strong>
              )}
              <CustomDataGrid
                tableId="ngX2Connection"
                rows={rowsData}
                columns={ngX2connectionsColumns}
                checkboxSelection={true}
                rowCount={noOfRows}
                loading={isLoading}
                disableColumnMenu={true}
                disableRowSelectionOnClick={true}
                onRowSelectionModelChange={handleOnRowSelected}
                getRowId={(row: IngpeerInterfaces) => row.index!}
                rowSelectionModel={selectionModel}
                isRowSelectable={() => !isOfflineDevice}
                sx={{
                  "& .MuiDataGrid-virtualScroller": { overflowY: "hidden" },
                  "& .MuiDataGrid-scrollbar--vertical": { display: "none" },
                }}
                fillContainer={false}
              />
            </>
          </div>
        </div>
      </div>
    </>
  );
};

export default NgX2Connections;
